import React, { Component } from 'react';
import {
    Descriptions,
    Tag,
    Button,
    Space,
    Card,
    Row,
    Col,
    Statistic,
    Progress,
    Typography,
    Divider,
    Timeline,
    Avatar
} from 'antd';
import {
    EditOutlined,
    GlobalOutlined,
    MobileOutlined,
    DesktopOutlined,
    TabletOutlined,
    PlayCircleOutlined,
    PictureOutlined,
    AppstoreOutlined,
    DollarOutlined,
    EyeOutlined,
    BarChartOutlined,
    RiseOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    RightSquareOutlined,
    GiftOutlined,
} from '@ant-design/icons';
import { getAdFormatLabel, normalizeAdFormatFamily } from '../adFormatUtils';

const { Text, Title } = Typography;

const AD_PLAN_OPTIONS = ['no_plan', 'advs', 'no_advs', 'avod'];

const normalizeAdPlans = (value) => {
    if (!value) {
        return [];
    }
    const arrayValue = Array.isArray(value) ? value : [value];
    return Array.from(
        new Set(
            arrayValue
                .filter(Boolean)
                .filter(plan => AD_PLAN_OPTIONS.includes(plan))
        )
    );
};

class AdUnitDetail extends Component {
    getPlatformIcon = (platform) => {
        const icons = {
            web: <GlobalOutlined />,
            ios: <MobileOutlined />,
            android: <MobileOutlined />,
            huawei: <TabletOutlined />
        };
        return icons[platform] || <DesktopOutlined />;
    };

    getFormatIcon = (format) => {
        const icons = {
            banner: <PictureOutlined />,
            interstitial: <AppstoreOutlined />,
            instream_video: <PlayCircleOutlined />,
            web_native: <GlobalOutlined />,
            app_native: <MobileOutlined />,
            app_open: <RightSquareOutlined />,
            rewarded: <GiftOutlined />,
        };
        return icons[normalizeAdFormatFamily(format)] || <PictureOutlined />;
    };

    getStatusColor = (status) => {
        const colors = {
            active: 'success',
            revoked: 'default',
            submitted: 'processing',
            rejected: 'error'
        };
        return colors[status] || 'default';
    };

    getAdPlanColor = (plan) => {
        const colors = {
            no_plan: 'default',
            advs: 'green',
            no_advs: 'orange',
        };
        return colors[plan] || 'default';
    };

    renderPerformanceCards = () => {
        const { adUnit } = this.props;

        return (
            <Row gutter={[16, 16]} className="performance-cards">
                <Col xs={12} sm={6}>
                    <Card className="performance-card">
                        <Statistic
                            title="Revenue"
                            value={adUnit.revenue || 0}
                            precision={2}
                            prefix={<DollarOutlined />}
                            suffix="USD"
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card className="performance-card">
                        <Statistic
                            title="Impressions"
                            value={adUnit.impressions || 0}
                            prefix={<EyeOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card className="performance-card">
                        <Statistic
                            title="Clicks"
                            value={adUnit.clicks || 0}
                            prefix={<BarChartOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card className="performance-card">
                        <Statistic
                            title="CTR"
                            value={adUnit.ctr || 0}
                            precision={2}
                            suffix="%"
                            prefix={<RiseOutlined />}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
            </Row>
        );
    };

    renderBasicInfo = () => {
        const { adUnit } = this.props;

        return (
            <Card title="Basic Information" className="detail-card">
                <Descriptions column={2} size="middle">
                    <Descriptions.Item label="Asset ID">
                        <Text code strong>{adUnit.assetId}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag color={this.getStatusColor(adUnit.status)}>
                            {adUnit.status?.toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Name">
                        <Text strong>{adUnit.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Business Unit">
                        <Tag color="blue">{adUnit.businessUnit}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Platform">
                        <Space>
                            {this.getPlatformIcon(adUnit.platform)}
                            <Text>{adUnit.platform}</Text>
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Ad Format">
                        <Space>
                            {this.getFormatIcon(adUnit.adFormat)}
                            <Text>{getAdFormatLabel(adUnit.adFormat)}</Text>
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Placement">
                        {adUnit.adPlacement
                            ? <Text>{adUnit.adPlacement}</Text>
                            : <Text type="secondary">N/A</Text>
                        }
                    </Descriptions.Item>
                    <Descriptions.Item label="Ad Plans">
                        {(() => {
                            const plans = normalizeAdPlans(adUnit.adPlans);
                            if (!plans.length) {
                                return <Text type="secondary">None</Text>;
                            }
                            return (
                                <Space size={[4, 4]} wrap>
                                    {plans.map(plan => (
                                        <Tag key={plan} color={this.getAdPlanColor(plan)}>
                                            {plan.replace(/_/g, ' ')}
                                        </Tag>
                                    ))}
                                </Space>
                            );
                        })()}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ad Type">
                        <Tag color={adUnit.adType === 'mature' ? 'red' : 'blue'}>
                            {adUnit.adType}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created Date">
                        <Text>{adUnit.created_date}</Text>
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        );
    };

    renderPartnerInfo = () => {
        const { adUnit, isSuperAdmin } = this.props;

        if (!isSuperAdmin) {
            return null;
        }

        return (
            <Card title="Partner Information" className="detail-card">
                <Descriptions column={1} size="middle">
                    <Descriptions.Item label="Ad Partner">
                        <Text>{adUnit.adPartner || 'Not specified'}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Partner App ID">
                        <Text>{adUnit.adPartnerAppId || 'Not specified'}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Partner Unit Path">
                        <Text>{adUnit.adPartnerUnitPath || 'Not specified'}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Partner Unit ID">
                        <Text code>{adUnit.adPartnerUnitId || 'Not specified'}</Text>
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        );
    };

    renderActivityTimeline = () => {
        const { adUnit } = this.props;

        // Mock activity data
        const activities = [
            {
                time: '2024-09-20 10:30',
                title: 'Ad Unit Created',
                description: `Ad unit "${adUnit.name}" was created`,
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
            },
            {
                time: '2024-09-20 10:35',
                title: 'Status Updated',
                description: 'Status changed to Submitted for review',
                icon: <ClockCircleOutlined style={{ color: '#1890ff' }} />
            },
            {
                time: '2024-09-21 09:15',
                title: 'Review Completed',
                description: 'Ad unit approved and activated',
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
            }
        ];

        if (adUnit.status === 'rejected') {
            activities.push({
                time: '2024-09-22 14:20',
                title: 'Issue Detected',
                description: 'Ad unit failed validation checks',
                icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            });
        }

        return (
            <Card title="Activity Timeline" className="detail-card">
                <Timeline>
                    {activities.map((activity, index) => (
                        <Timeline.Item key={index} dot={activity.icon}>
                            <div className="timeline-content">
                                <div className="timeline-title">{activity.title}</div>
                                <div className="timeline-description">{activity.description}</div>
                                <div className="timeline-time">{activity.time}</div>
                            </div>
                        </Timeline.Item>
                    ))}
                </Timeline>
            </Card>
        );
    };

    renderPerformanceMetrics = () => {
        const { adUnit } = this.props;
        const cpm = adUnit.cpm || 0;
        const fillRate = Math.random() * 100; // Mock fill rate
        const viewability = Math.random() * 100; // Mock viewability

        return (
            <Card title="Performance Metrics" className="detail-card">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                        <div className="metric-item">
                            <Text type="secondary">CPM</Text>
                            <div className="metric-value">
                                <Text strong>${cpm.toFixed(2)}</Text>
                            </div>
                            <Progress
                                percent={Math.min((cpm / 10) * 100, 100)}
                                showInfo={false}
                                strokeColor="#52c41a"
                                size="small"
                            />
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div className="metric-item">
                            <Text type="secondary">Fill Rate</Text>
                            <div className="metric-value">
                                <Text strong>{fillRate.toFixed(1)}%</Text>
                            </div>
                            <Progress
                                percent={fillRate}
                                showInfo={false}
                                strokeColor="#1890ff"
                                size="small"
                            />
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div className="metric-item">
                            <Text type="secondary">Viewability</Text>
                            <div className="metric-value">
                                <Text strong>{viewability.toFixed(1)}%</Text>
                            </div>
                            <Progress
                                percent={viewability}
                                showInfo={false}
                                strokeColor="#722ed1"
                                size="small"
                            />
                        </div>
                    </Col>
                </Row>
            </Card>
        );
    };

    render() {
        const { adUnit, onEdit, isSuperAdmin } = this.props;

        return (
            <div className="adunit-detail">
                <div className="detail-header">
                    <div className="header-content">
                        <Title level={4}>{adUnit.name}</Title>
                        <Text type="secondary">{adUnit.assetId}</Text>
                    </div>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={onEdit}
                    >
                        Edit
                    </Button>
                </div>

                <Divider />

                {/* Performance Overview */}
                {this.renderPerformanceCards()}

                <Divider />

                {/* Basic Information */}
                {this.renderBasicInfo()}

                {/* Partner Information */}
                {isSuperAdmin && this.renderPartnerInfo()}

                {/* Performance Metrics */}
                {this.renderPerformanceMetrics()}

                {/* Activity Timeline */}
                {this.renderActivityTimeline()}
            </div>
        );
    }
}

export default AdUnitDetail;
